"use client";

import Link from "next/link";
import { SectionHeader, StaggerGroup, StaggerItem } from "../shared";
import {
  HomeIcon,
  WalletIcon,
  PhoneIcon,
  LinkIcon,
  HeartIcon,
  MegaphoneIcon,
  RssIcon,
  UserIcon,
  GiftIcon,
  FlameIcon,
} from "../shared/icons";

type ExploreItem = {
  href: string;
  label: string;
  description: string;
  icon: typeof HomeIcon;
  accent: string;
};

const ITEMS: ExploreItem[] = [
  { href: "/", label: "Home", description: "Dashboard", icon: HomeIcon, accent: "#00D4FF" },
  { href: "/wallet-analysis", label: "Wallet Analysis", description: "Analyze wallets", icon: WalletIcon, accent: "#A855F7" },
  { href: "/phone-book", label: "Phone Book", description: "Saved contacts", icon: PhoneIcon, accent: "#3B82F6" },
  { href: "/url-threat-scan", label: "URL Threat Scan", description: "Check safety", icon: LinkIcon, accent: "#00D4FF" },
  { href: "/charities", label: "Charities", description: "Find charities", icon: HeartIcon, accent: "#A855F7" },
  { href: "/fundraisers", label: "Fundraisers", description: "Active campaigns", icon: MegaphoneIcon, accent: "#F87171" },
  { href: "/transactions/live", label: "Live Feed", description: "Real-time activity", icon: RssIcon, accent: "#00D4FF" },
  { href: "/profile", label: "Profile", description: "Your profile", icon: UserIcon, accent: "#22C55E" },
  { href: "/donate", label: "Donate", description: "Support causes", icon: GiftIcon, accent: "#A855F7" },
  { href: "/trending", label: "Trending", description: "Top activity", icon: FlameIcon, accent: "#F97316" },
];

/**
 * "Explore Reqrium" directory grid - 10 tiles linking straight to the
 * platform's real, existing routes (no new pages invented).
 *
 * 2026-08-04 premium pass (client, round 2): these are now compact
 * `.pr-tile` surfaces (see styles/globals.css) rather than the full-size
 * `GlassCard` the feature cards above use. Three things changed on
 * request:
 *
 *  - Smaller. A ten-item directory should read as a quiet, uniform set of
 *    targets, not ten hero cards. Padding, badge, and type all came down.
 *  - Darker, glassier surface. Each tile is a near-black three-stop
 *    gradient behind a 20px backdrop blur, so it sits *into* the page
 *    rather than floating on it as a lighter panel.
 *  - Dynamic per-tile accent. The item's colour drives its icon well, its
 *    hover bloom, and its underline, which gives the grid movement without
 *    ten competing card treatments.
 *
 * The icon keeps a heavier 2.1 stroke weight: at 20px the icon set's 1.75
 * default reads as hairline-thin, which was the original "icons don't
 * display well" note.
 */
export default function ExploreGrid() {
  return (
    <section aria-label="Explore Reqrium" className="page-section relative w-full py-10 sm:py-14">
      <div className="mx-auto max-w-6xl">
        <SectionHeader
          title="Explore Reqrium"
          viewAllHref="/search"
          description="Every surface of the platform, one click away — analysis, verification, live movement, and giving."
        />

        <StaggerGroup className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-3.5 lg:grid-cols-5">
          {ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <StaggerItem key={item.href}>
                <Link href={item.href} className="pr-tile">
                  <span
                    className="pr-tile-bloom"
                    style={{
                      background: `radial-gradient(circle at 22% 0%, ${item.accent}2E, transparent 62%)`,
                    }}
                    aria-hidden="true"
                  />
                  <span
                    className="pr-tile-badge"
                    style={{
                      color: item.accent,
                      borderColor: `${item.accent}40`,
                      background: `linear-gradient(155deg, ${item.accent}2B 0%, ${item.accent}0F 58%, rgba(3,8,18,0.85) 100%)`,
                      boxShadow: `inset 0 1px 0 rgba(255,255,255,0.12), 0 10px 22px -16px ${item.accent}A6`,
                    }}
                    aria-hidden="true"
                  >
                    <Icon size={20} strokeWidth={2.1} />
                  </span>

                  <span className="min-w-0">
                    <span className="pr-tile-label">{item.label}</span>
                    <span className="pr-tile-desc">{item.description}</span>
                  </span>

                  {/* Underline that extends in the tile's own accent on hover -
                      a quiet directional cue that the tile is interactive,
                      without adding a tenth arrow glyph to the grid. */}
                  <span
                    className="pr-tile-bar"
                    style={{ background: `linear-gradient(90deg, ${item.accent}, ${item.accent}2E)` }}
                    aria-hidden="true"
                  />
                </Link>
              </StaggerItem>
            );
          })}
        </StaggerGroup>
      </div>
    </section>
  );
}
